import { Component, OnInit, Input, inject } from '@angular/core';
import { CurrentStateService, DirtyFlagService } from '@ddv/behaviors';
import { AdditionalFiltersDropdownComponent, DashboardFiltersComponent, RefreshRestoreComponent, WidgetQueryparamService } from '@ddv/dashboards';
import { QueryParamsService } from '@ddv/filters';
import { LayoutService, ManagerService } from '@ddv/layout';
import {
    Client,
    MODE,
    FilterPreference,
    Fund,
    FilterQueryParams,
    FuzzyDates,
    AdditionalFilter,
    AppWidgetState,
    NamedQuery,
    UserPreferences,
} from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { WidgetService } from '@ddv/widgets';
import { FuzzyDate } from '@hs/ui-core-date';
import { Fund as CcFund } from '@hs/ui-core-fund';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-widget-filter',
    templateUrl: './widget-filter.component.html',
    styleUrls: ['./widget-filter.component.scss'],
    imports: [
        DashboardFiltersComponent,
        AdditionalFiltersDropdownComponent,
        RefreshRestoreComponent,
    ],
})
export class WidgetFilterComponent implements OnInit {
    @Input() widgetId: number | undefined;
    @Input() isSubscribedToDashboardFilters = false;
    @Input() fuzzyDates: FuzzyDates = new FuzzyDates();
    @Input() ccFuzzyDates: FuzzyDate[] = [];
    @Input() clients: Client[] = [];
    @Input() userPreferences: UserPreferences | undefined;
    @Input() funds: CcFund[] = [];

    private readonly manager = inject(ManagerService);
    private readonly widgetService = inject(WidgetService);
    private readonly dirtyFlagService = inject(DirtyFlagService);
    private readonly widgetQueryparamService = inject(WidgetQueryparamService);
    private readonly layoutService = inject(LayoutService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly currentStateService = inject(CurrentStateService);
    private readonly namedQueriesService = inject(NamedQueriesService);

    protected clientCode: string | undefined;
    protected isMultiClient = false;
    protected queryParams: FilterPreference | undefined;
    protected namedQuery: NamedQuery | undefined;
    protected widgetFilters: FilterPreference = { funds: [], filters: [] };
    protected nonUdfFilters: AdditionalFilter[] = [];

    private isStackedQuery = false;
    private namedQuerySubscription: Subscription | undefined;

    ngOnInit(): void {
        this.currentStateService.isMultiClient$
            .subscribe({
                next: (isMultiClient: boolean) => this.isMultiClient = isMultiClient,
            });

        const widgetPrefs = this.manager.getWidgetPreferences(this.widgetId ?? 0);
        if (widgetPrefs) {
            this.clientCode = widgetPrefs.clientCode;

            if (widgetPrefs?.namedQueryId) {
                this.namedQuerySubscription?.unsubscribe();
                this.setNamedQuerySubscription(widgetPrefs);
            }
        }

        this.widgetFilters = this.widgetQueryparamService.widgetFilterParams.get(this.widgetId ?? 0) ?? { funds: [], filters: [] };
        this.nonUdfFilters = this.widgetFilters.filters?.filter((f) => !f.name?.startsWith('udf_')) ?? [];
    }

    onApplySelectedFunds(ccFunds: CcFund[]): void {
        const funds = this.funds.filter((fund) => ccFunds.some((ccFund) => ccFund.code === fund.code));
        this.widgetFilters.funds = funds.map((fund) => new Fund(fund, this.userPreferences?.fundNamesSource ?? ''));
        this.updateWidgetQueryParams();
    }

    onApplySelectedClients(clients: Client[]): void {
        this.widgetFilters.clients = clients;
        this.updateWidgetQueryParams();
    }

    onApplyDateChanges(filterDate: FilterQueryParams | FuzzyDates): void {
        Object.keys(filterDate).forEach((key) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.widgetFilters as any)[key] = this.isStackedQuery ? undefined : (filterDate as any)[key];
        });
        this.updateWidgetQueryParams();
    }

    onDbFiltersApplied(additionalFilters: AdditionalFilter[]): void {
        this.widgetFilters.filters = additionalFilters;
        this.updateWidgetQueryParams();
    }

    updateWidgetQueryParams(): void {
        if (this.layoutService.getWorkspaceMode() === MODE.EDIT_WORKSPACE) {
            this.dirtyFlagService.enterDirtyState(this.manager.getCurrentDashboardId() ?? '');
        }

        const preference = this.manager.getWidgetPreferences(this.widgetId ?? 0);
        if (preference) {
            preference.widgetFilters = this.widgetFilters;
            preference.isActiveDate = this.widgetFilters.activeDate != null;
            this.manager.setWidgetExtraPreferences(preference.id, preference);
        }
        this.queryParamsService.addWidgetQueryParam(this.widgetId ?? 0, this.widgetFilters);
    }

    onRestoreCurrentWidget(): void {
        const preference = this.manager.getWidgetPreferences(this.widgetId ?? 0);
        if (!preference) {
            return;
        }
        this.widgetService.getWidgetPreferences(preference.coreWidgetId ?? 0, preference.clientCode ?? '').subscribe((widgetPrefs) => {
            preference.widgetFilters = widgetPrefs.widgetFilters;
            this.widgetFilters = preference.widgetFilters ?? { funds: [], filters: [] };
            this.updateWidgetQueryParams();
        });
    }

    onRefreshCurrentWidget(): void {
        this.updateWidgetQueryParams();
    }

    private setNamedQuerySubscription(widgetPrefs: AppWidgetState): void {
        this.namedQuerySubscription = this.namedQueriesService.fetchNamedQuery(widgetPrefs.namedQueryId)
            .subscribe({
                next: (namedQuery) => {
                    this.namedQuery = namedQuery;
                    this.isStackedQuery = !!namedQuery?.normalizeQueryIsStacked();
                },
            });
    }
}
